import { ExtranetOrderTicketDetailsDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacCheckbox,
  PharmacIcon,
  PharmacSuccessToast,
  PharmacTableColumn,
  PharmacText,
  PharmacTooltip,
  usePharmacToast,
} from '@pharmac/ui'
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { CatalogItemAvailability } from '../dose-dispensing-catalog/CatalogItemAvailability'
import {
  getOrderTicketsQueryKeyBase,
  getOrderTicketsQueryKeyFactory,
} from './util/order-ticket-query-key-factory'

type Props = {
  requestChecked?: boolean
  ticketId: number
}

export const OrderTicketOutOfStockProductsTable = ({ requestChecked, ticketId }: Props) => {
  const { maxMobile } = useBreakpoint()
  const [checked, updateChecked] = useState<boolean>(false)
  const { isPharmacCustomerService } = useAuthenticationContext()
  const { customer } = useCustomerContext()
  const { key } = useLanguage()
  const queryClient = useQueryClient()
  const toastHook = usePharmacToast()
  const navigate = useNavigate()

  const getOrderTicketById = (
    ticketId?: number,
    customerId?: number
  ): UseQueryOptions<ExtranetOrderTicketDetailsDto> => ({
    queryFn: async () =>
      await api.orders.getOrderTicketById(ticketId as number, customerId as number),
    queryKey: getOrderTicketsQueryKeyFactory.ticketById(customerId, ticketId),
    enabled: typeof customerId === 'number' && typeof ticketId === 'number',
  })
  const { data: ticketById } = useQuery(getOrderTicketById(ticketId, customer?.id))

  const { mutate: confirmTicket } = useMutation({
    mutationFn: () => api.orders.confirmOrderTicket(Number(customer?.id), Number(ticketById?.id)),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('extranet:correctionRequests.requesCheckedSuccess')}
          </PharmacSuccessToast>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: getOrderTicketsQueryKeyBase(customer?.id),
      })
      navigate(0)
    },
  })

  return (
    <ExtranetContainer
      full
      flexColumn
    >
      <ExtranetTable>
        <ExtranetTableRow
          noBorders
          noMargin
          style={{
            backgroundColor: 'var(--color-accent-2)',
            border: 'none',
            fontWeight: 600,
          }}
        >
          <ExtranetTableColumn
            size={6}
            marginSmallLeft
          >
            {t('extranet:correctionRequests.missingProduct')}
          </ExtranetTableColumn>
          <ExtranetTableColumn size={3}>
            {t('extranet:medicines.availableForCustomers')}
          </ExtranetTableColumn>
          <ExtranetTableColumn size={6}>
            {t('extranet:medicines.substituteProduct')}
          </ExtranetTableColumn>
        </ExtranetTableRow>

        {ticketById?.outOfStockProducts?.map((product, i) => (
          <ExtranetContainer
            key={i}
            full
            flexColumn
            style={{
              borderTop: i > 0 ? '2px dashed var(--color-accent-4)' : undefined,
              backgroundColor: 'var(--color-accent-1)',
            }}
          >
            <ExtranetTableRow
              noBorders
              noMargin
              key={product.outOfStockProductNumber}
              style={{
                marginTop: 'var(--spacing-small)',
                marginBottom: 'var(--spacing-small)',
              }}
              toggleExpand={() => null}
              children={() => (
                <>
                  <PharmacTableColumn size={6}>
                    <ExtranetContainer marginSmallLeft>
                      <PharmacText>
                        {product.outOfStockProductNumber} {key(product.outOfStockProductName)}{' '}
                        {product.strength}
                      </PharmacText>
                    </ExtranetContainer>
                  </PharmacTableColumn>
                  <PharmacTableColumn size={3}>
                    <ExtranetContainer alignCenter>
                      <CatalogItemAvailability
                        availability={product.outOfStockProductAvailability}
                      />
                    </ExtranetContainer>
                  </PharmacTableColumn>
                  <PharmacTableColumn size={6}>
                    <PharmacText>
                      {product.substituteProductNumber} {key(product.substituteProductName)}{' '}
                      {product.substituteProductStrength}
                    </PharmacText>
                  </PharmacTableColumn>
                </>
              )}
            />
            {product?.comment && (
              <ExtranetContainer
                full
                flexColumn
                paddingMediumHorizontal
                paddingMediumVertical
              >
                <>
                  <PharmacText weight="semibold">{t('common:comment')}</PharmacText>
                  <PharmacText>{product.comment}</PharmacText>
                </>
              </ExtranetContainer>
            )}
          </ExtranetContainer>
        ))}
      </ExtranetTable>
      <ExtranetContainer
        full
        flexRow
        alignCenter
        justifyEnd
        paddingMediumTop
        style={{
          backgroundColor: 'var(--color-accent-1)',
        }}
      >
        {requestChecked && (
          <ExtranetContainer flexRow>
            <PharmacText marginSmallRight>
              {t('extranet:correctionRequests.requesCheckedBy')}:
            </PharmacText>
            <PharmacText
              marginSmallRight
              weight="semibold"
            >
              {ticketById?.isExpired && (
                <>
                  {ticketById?.expiredAt &&
                    formatDateTimeString(
                      new Date(ticketById!.expiredAt as string).toISOString()
                    )}{' '}
                  {'Pharmac Finland Oy'}
                </>
              )}
              {ticketById?.customerSignature && (
                <>
                  {ticketById?.confirmedByCustomerAt &&
                    formatDateTimeString(ticketById?.confirmedByCustomerAt)}{' '}
                  {ticketById?.customerSignature}
                </>
              )}
            </PharmacText>
          </ExtranetContainer>
        )}
        <PharmacTooltip
          marginMediumLeft
          marginMediumRight
          maxWidth={maxMobile ? '295px' : '500px'}
          handle={
            <PharmacIcon
              style={{ color: 'var(--color-info)' }}
              size="25px"
              icon="info"
            />
          }
        >
          <PharmacText>{t('extranet:correctionRequests.requestCheckTooltip')}</PharmacText>
        </PharmacTooltip>
        <ExtranetContainer
          fourth
          paddingSmallVertical
          paddingMediumHorizontal
          alignCenter
          style={{
            cursor: requestChecked ? 'not-allowed' : undefined,
            backgroundColor: requestChecked ? 'var(--color-accent-4)' : ' var(--color-yellow)',
            borderRadius: '15px 0 0',
            width: 'fit-content',
          }}
        >
          <PharmacCheckbox
            style={{
              cursor: requestChecked ? 'not-allowed' : undefined,
              opacity: requestChecked ? '0.8' : undefined,
            }}
            checked={requestChecked ? true : checked}
            disabled={requestChecked || isPharmacCustomerService ? true : false}
            handleChange={(event) => {
              updateChecked(event.target.checked), confirmTicket()
            }}
          >
            <PharmacText
              weight="bold"
              marginSmallLeft
              style={{
                opacity: requestChecked ? '0.6' : undefined,
              }}
            >
              {ticketById?.isExpired ? (
                <>{t('extranet:correctionRequests.requestCheckByPharmac')}</>
              ) : (
                <>{t('extranet:correctionRequests.requestCheck')}</>
              )}
            </PharmacText>
          </PharmacCheckbox>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
